// telas/SuasDinamicas.tsx - As dinâmicas já criadas (o histórico de hoje, com a apresentação nova).
//
// Abaixo do pedido: título e data de cada uma, "Abrir" para ver de novo e "Remover" com confirmação.
// O "Limpar histórico" em massa sai: apagar tudo de uma vez é o que mais assusta e o que menos se usa.

import { useRef, useState } from 'react';
import { Button, ConfirmDialog, IconTrash, Skeleton } from '@winieduca/ui';
import type { AtividadeHistorico } from '../types/index';
import { historico } from '../content/textos';
import { semMarcas } from './TextoGerado';

const DATA = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Sao_Paulo' });

export type ListaDeDinamicas = { estado: 'carregando' } | { estado: 'falha' } | { estado: 'pronta'; itens: AtividadeHistorico[] };

export function SuasDinamicas({
  lista,
  onAbrir,
  onRemover,
}: {
  lista: ListaDeDinamicas;
  onAbrir: (a: AtividadeHistorico) => void;
  onRemover: (id: string) => Promise<boolean>;
}) {
  const [remover, setRemover] = useState<AtividadeHistorico | null>(null);
  const [falhou, setFalhou] = useState<string | null>(null);
  const titulo = useRef<HTMLHeadingElement>(null);

  async function confirmarRemocao() {
    const alvo = remover;
    setRemover(null);
    if (!alvo) return;
    setFalhou(null);
    if (await onRemover(alvo.id)) {
      // O botão que abriu o diálogo sai junto com a dinâmica: o foco vai para o título da lista.
      titulo.current?.focus();
    } else {
      setFalhou(alvo.id);
    }
  }

  // Só as que têm o resultado guardado: sem ele, não há o que abrir.
  const itens = lista.estado === 'pronta' ? lista.itens.filter((a) => a.resultado?.dinamica) : [];
  // O diálogo pede a pergunta no título e a consequência abaixo (DESIGN_SYSTEM §6).
  const fim = historico.confirmar.indexOf('?') + 1;

  return (
    <section className="din-suas" aria-labelledby="suas-titulo">
      <h2 id="suas-titulo" className="we-h2" ref={titulo} tabIndex={-1}>
        {historico.titulo}
      </h2>
      {lista.estado === 'carregando' && <Skeleton lines={2} />}
      {lista.estado === 'falha' && <p className="din-aviso">{historico.falha}</p>}
      {lista.estado === 'pronta' && itens.length === 0 && <p className="din-aviso">{historico.vazio}</p>}
      {itens.length > 0 && (
        <ul className="din-suas__lista">
          {itens.map((a) => (
            <li key={a.id} className="din-suas__item">
              <div className="din-suas__texto">
                <span className="din-suas__titulo">{semMarcas(a.resultado.dinamica.titulo)}</span>
                <time className="din-suas__data" dateTime={a.timestamp}>
                  {DATA.format(new Date(a.timestamp))}
                </time>
                {falhou === a.id && (
                  <span className="din-erro" role="alert">
                    {historico.removerFalha}
                  </span>
                )}
              </div>
              <div className="din-suas__acoes">
                <Button onClick={() => onAbrir(a)}>
                  {historico.abrir}
                </Button>
                <Button variant="quiet" icon={IconTrash} onClick={() => setRemover(a)} aria-label={`${historico.remover}: ${semMarcas(a.resultado.dinamica.titulo)}`}>
                  {historico.remover}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={remover !== null}
        title={historico.confirmar.slice(0, fim)}
        confirmLabel={historico.remover}
        cancelLabel={historico.cancelar}
        onConfirm={() => void confirmarRemocao()}
        onCancel={() => setRemover(null)}
      >
        {historico.confirmar.slice(fim).trim()}
      </ConfirmDialog>
    </section>
  );
}
